import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";

const api = new API();

/*
|--------------------------------------------------------------------------
| DEFAULT FILTERS
|--------------------------------------------------------------------------
*/

const initialFilters = {
  fromDate: "",
  toDate: "",

  search: "",

  customerId: "",
  salespersonId: "",

  branchId: "",
  firmId: "",
  warehouseId: "",

  documentStatus: "CONFIRMED",
  paymentStatus: "",
  salesChannel: "",
  sourceType: "",

  paymentMethod: "",
  paymentType: "",
  direction: "IN",
  status: "",

  refundStatus: "",
  refundMethod: "",
  returnType: "",

  groupBy: "DAY",
  overdueOnly: false,

  page: 1,
  limit: 20,
};

/*
|--------------------------------------------------------------------------
| DEFAULT PAGINATION
|--------------------------------------------------------------------------
*/

const initialPagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/*
|--------------------------------------------------------------------------
| DEFAULT OVERVIEW
|--------------------------------------------------------------------------
*/

const initialOverview = {
  dateRange: null,

  invoiceCount: 0,
  totalQuantity: 0,

  grossSales: 0,
  itemDiscount: 0,
  invoiceDiscount: 0,
  totalDiscount: 0,

  taxableAmount: 0,
  taxAmount: 0,

  totalSales: 0,
  returnCount: 0,
  returnAmount: 0,
  netSales: 0,

  receivedAmount: 0,
  pendingAmount: 0,
  refundedAmount: 0,

  paymentCount: 0,
  actualPaymentReceived: 0,

  averageInvoiceValue: 0,
};

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
  filters: {
    ...initialFilters,
  },

  dropdowns: {
    documentTypes: [],
    salesChannels: [],
    paymentMethods: [],
    paymentStatuses: [],
    groupings: [],
    documentStatuses: [],
    refundStatuses: [],
    returnTypes: [],
    paymentDirections: [],
  },

  overview: {
    ...initialOverview,
  },

  salesTrend: {
    dateRange: null,
    groupBy: "DAY",
    records: [],
  },

  salesChannel: {
    dateRange: null,
    records: [],
    grandTotal: 0,
  },

  paymentMethod: {
    dateRange: null,
    records: [],
    grandTotal: 0,
  },

  outstandingSales: {
    dateRange: null,
    records: [],

    summary: {
      invoiceCount: 0,
      invoiceAmount: 0,
      receivedAmount: 0,
      outstandingAmount: 0,
    },

    pagination: {
      ...initialPagination,
    },
  },

  customerSales: {
    dateRange: null,
    records: [],

    summary: {
      customerCount: 0,
      invoiceCount: 0,
      totalSales: 0,
      paidAmount: 0,
      outstandingAmount: 0,
    },

    pagination: {
      ...initialPagination,
    },
  },

  productSales: {
    dateRange: null,
    records: [],

    summary: {
      productCount: 0,
      quantitySold: 0,
      salesAmount: 0,
      taxableAmount: 0,
      taxAmount: 0,
      purchaseCost: 0,
      estimatedProfit: 0,
    },

    pagination: {
      ...initialPagination,
    },
  },

  taxSummary: {
    dateRange: null,
    records: [],

    summary: {
      quantity: 0,
      taxableAmount: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      cessAmount: 0,
      totalTaxAmount: 0,
      totalAmount: 0,
    },
  },

  saleReturnReport: {
    dateRange: null,
    records: [],

    summary: {
      returnCount: 0,
      returnedQuantity: 0,
      taxableAmount: 0,
      taxAmount: 0,
      returnAmount: 0,
      refundedAmount: 0,
      restockingFee: 0,
    },

    pagination: {
      ...initialPagination,
    },
  },

  documentTypeReport: {
    dateRange: null,
    records: [],
  },

  dropdownsLoading: false,
  overviewLoading: false,
  salesTrendLoading: false,
  salesChannelLoading: false,
  paymentMethodLoading: false,
  outstandingSalesLoading: false,
  customerSalesLoading: false,
  productSalesLoading: false,
  taxSummaryLoading: false,
  saleReturnReportLoading: false,
  documentTypeReportLoading: false,

  error: null,
};

/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
*/

const getResponseData = (
  response,
  fallback = {}
) => {
  if (!response) {
    return fallback;
  }

  return (
    response.data ||
    response.result ||
    response
  );
};

const getErrorMessage = (
  response,
  fallbackMessage
) => {
  return (
    response?.message ||
    response?.error?.message ||
    response?.error ||
    fallbackMessage
  );
};

const rejectIfFailed = (
  response,
  rejectWithValue,
  fallbackMessage
) => {
  if (
    !response ||
    response.success === false ||
    response.status === "error"
  ) {
    return rejectWithValue(
      getErrorMessage(
        response,
        fallbackMessage
      )
    );
  }

  return null;
};

/*
|--------------------------------------------------------------------------
| BUILD REPORT PARAMS
|--------------------------------------------------------------------------
*/

const buildReportParams = (
  state,
  params = {},
  options = {}
) => {
  const filters =
    state?.billingReport
      ?.filters ||
    initialFilters;

  const result = {
    ...filters,
    ...params,
  };

  if (options.removePagination) {
    delete result.page;
    delete result.limit;
  }

  if (options.removePaymentFields) {
    delete result.paymentMethod;
    delete result.paymentType;
    delete result.direction;
    delete result.status;
  }

  if (options.removeReturnFields) {
    delete result.refundStatus;
    delete result.refundMethod;
    delete result.returnType;
  }

  return result;
};

/*
|--------------------------------------------------------------------------
| FETCH DROPDOWNS
|--------------------------------------------------------------------------
*/

export const fetchBillingReportDropdowns =
  createAsyncThunk(
    "billingReport/fetchBillingReportDropdowns",
    async (
      _,
      {
        rejectWithValue,
      }
    ) => {
      const response =
        await api.get(
          config.getBillingReportDropdowns
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch billing report dropdowns."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| SALES OVERVIEW
|--------------------------------------------------------------------------
*/

export const fetchSalesOverviewReport =
  createAsyncThunk(
    "billingReport/fetchSalesOverviewReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePagination: true,
            removePaymentFields: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getSalesOverviewReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch sales overview report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| SALES TREND
|--------------------------------------------------------------------------
*/

export const fetchSalesTrendReport =
  createAsyncThunk(
    "billingReport/fetchSalesTrendReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePagination: true,
            removePaymentFields: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getSalesTrendReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch sales trend report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| SALES CHANNEL
|--------------------------------------------------------------------------
*/

export const fetchSalesChannelReport =
  createAsyncThunk(
    "billingReport/fetchSalesChannelReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePagination: true,
            removePaymentFields: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getSalesChannelReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch sales channel report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| PAYMENT METHOD REPORT
|--------------------------------------------------------------------------
*/

export const fetchPaymentMethodReport =
  createAsyncThunk(
    "billingReport/fetchPaymentMethodReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePagination: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getPaymentMethodReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch payment method report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| OUTSTANDING SALES
|--------------------------------------------------------------------------
*/

export const fetchOutstandingSalesReport =
  createAsyncThunk(
    "billingReport/fetchOutstandingSalesReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePaymentFields: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getOutstandingSalesReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch outstanding sales report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| CUSTOMER SALES
|--------------------------------------------------------------------------
*/

export const fetchCustomerSalesReport =
  createAsyncThunk(
    "billingReport/fetchCustomerSalesReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePaymentFields: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getCustomerSalesReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch customer sales report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| PRODUCT SALES
|--------------------------------------------------------------------------
*/

export const fetchProductSalesReport =
  createAsyncThunk(
    "billingReport/fetchProductSalesReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePaymentFields: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getProductSalesReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch product sales report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| TAX SUMMARY
|--------------------------------------------------------------------------
*/

export const fetchTaxSummaryReport =
  createAsyncThunk(
    "billingReport/fetchTaxSummaryReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePagination: true,
            removePaymentFields: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getTaxSummaryReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch tax summary report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| SALE RETURN REPORT
|--------------------------------------------------------------------------
*/

export const fetchSaleReturnReport =
  createAsyncThunk(
    "billingReport/fetchSaleReturnReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePaymentFields: true,
          }
        );

      const response =
        await api.get(
          config.getSaleReturnReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch sale return report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| DOCUMENT TYPE REPORT
|--------------------------------------------------------------------------
*/

export const fetchDocumentTypeReport =
  createAsyncThunk(
    "billingReport/fetchDocumentTypeReport",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const queryParams =
        buildReportParams(
          getState(),
          params,
          {
            removePagination: true,
            removePaymentFields: true,
            removeReturnFields: true,
          }
        );

      const response =
        await api.get(
          config.getDocumentTypeReport,
          queryParams
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch document type report."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH DASHBOARD REPORTS
|--------------------------------------------------------------------------
|
| This thunk loads the main report cards and charts together.
|--------------------------------------------------------------------------
*/

export const fetchBillingDashboardReports =
  createAsyncThunk(
    "billingReport/fetchBillingDashboardReports",
    async (
      params = {},
      {
        dispatch,
        rejectWithValue,
      }
    ) => {
      try {
        const results =
          await Promise.all([
            dispatch(
              fetchSalesOverviewReport(
                params
              )
            ),

            dispatch(
              fetchSalesTrendReport(
                params
              )
            ),

            dispatch(
              fetchSalesChannelReport(
                params
              )
            ),

            dispatch(
              fetchPaymentMethodReport(
                params
              )
            ),
          ]);

        const failedResult =
          results.find(
            (result) =>
              result.meta
                ?.requestStatus ===
              "rejected"
          );

        if (failedResult) {
          return rejectWithValue(
            failedResult.payload ||
            "Unable to fetch one or more billing dashboard reports."
          );
        }

        return {
          success: true,
        };
      } catch (error) {
        return rejectWithValue(
          error?.message ||
          "Unable to fetch billing dashboard reports."
        );
      }
    }
  );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const billingReportSlice =
  createSlice({
    name:
      "billingReport",

    initialState,

    reducers: {
      setBillingReportFilters: (
        state,
        action
      ) => {
        state.filters = {
          ...state.filters,
          ...(action.payload || {}),
        };
      },

      setBillingReportFilter: (
        state,
        action
      ) => {
        const {
          key,
          value,
        } =
          action.payload || {};

        if (!key) {
          return;
        }

        state.filters[key] =
          value;

        if (
          key !== "page" &&
          key !== "limit"
        ) {
          state.filters.page = 1;
        }
      },

      setBillingReportDateRange: (
        state,
        action
      ) => {
        const {
          fromDate = "",
          toDate = "",
        } =
          action.payload || {};

        state.filters.fromDate =
          fromDate;

        state.filters.toDate =
          toDate;

        state.filters.page = 1;
      },

      setBillingReportPage: (
        state,
        action
      ) => {
        state.filters.page =
          Number(
            action.payload
          ) || 1;
      },

      setBillingReportLimit: (
        state,
        action
      ) => {
        state.filters.limit =
          Number(
            action.payload
          ) || 20;

        state.filters.page = 1;
      },

      resetBillingReportFilters: (
        state
      ) => {
        state.filters = {
          ...initialFilters,
        };
      },

      clearBillingReportError: (
        state
      ) => {
        state.error = null;
      },

      clearSalesOverviewReport: (
        state
      ) => {
        state.overview = {
          ...initialOverview,
        };
      },

      clearSalesTrendReport: (
        state
      ) => {
        state.salesTrend = {
          dateRange: null,
          groupBy:
            state.filters.groupBy ||
            "DAY",
          records: [],
        };
      },

      clearOutstandingSalesReport: (
        state
      ) => {
        state.outstandingSales = {
          dateRange: null,
          records: [],

          summary: {
            invoiceCount: 0,
            invoiceAmount: 0,
            receivedAmount: 0,
            outstandingAmount: 0,
          },

          pagination: {
            ...initialPagination,
          },
        };
      },

      clearCustomerSalesReport: (
        state
      ) => {
        state.customerSales = {
          dateRange: null,
          records: [],

          summary: {
            customerCount: 0,
            invoiceCount: 0,
            totalSales: 0,
            paidAmount: 0,
            outstandingAmount: 0,
          },

          pagination: {
            ...initialPagination,
          },
        };
      },

      clearProductSalesReport: (
        state
      ) => {
        state.productSales = {
          dateRange: null,
          records: [],

          summary: {
            productCount: 0,
            quantitySold: 0,
            salesAmount: 0,
            taxableAmount: 0,
            taxAmount: 0,
            purchaseCost: 0,
            estimatedProfit: 0,
          },

          pagination: {
            ...initialPagination,
          },
        };
      },

      resetBillingReportState:
        () => ({
          ...initialState,

          filters: {
            ...initialFilters,
          },

          overview: {
            ...initialOverview,
          },
        }),
    },

    extraReducers: (
      builder
    ) => {
      /*
      |--------------------------------------------------------------------------
      | DROPDOWNS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingReportDropdowns.pending,
          (state) => {
            state.dropdownsLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingReportDropdowns.fulfilled,
          (
            state,
            action
          ) => {
            state.dropdownsLoading =
              false;

            const payload =
              action.payload || {};

            state.dropdowns = {
              ...state.dropdowns,
              ...(payload.dropdowns ||
                payload),
            };
          }
        )
        .addCase(
          fetchBillingReportDropdowns.rejected,
          (
            state,
            action
          ) => {
            state.dropdownsLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch billing report dropdowns.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | SALES OVERVIEW
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchSalesOverviewReport.pending,
          (state) => {
            state.overviewLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchSalesOverviewReport.fulfilled,
          (
            state,
            action
          ) => {
            state.overviewLoading =
              false;

            state.overview = {
              ...initialOverview,
              ...(action.payload ||
                {}),
            };
          }
        )
        .addCase(
          fetchSalesOverviewReport.rejected,
          (
            state,
            action
          ) => {
            state.overviewLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch sales overview report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | SALES TREND
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchSalesTrendReport.pending,
          (state) => {
            state.salesTrendLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchSalesTrendReport.fulfilled,
          (
            state,
            action
          ) => {
            state.salesTrendLoading =
              false;

            const payload =
              action.payload || {};

            state.salesTrend = {
              dateRange:
                payload.dateRange ||
                null,

              groupBy:
                payload.groupBy ||
                state.filters.groupBy ||
                "DAY",

              records:
                payload.records ||
                [],
            };
          }
        )
        .addCase(
          fetchSalesTrendReport.rejected,
          (
            state,
            action
          ) => {
            state.salesTrendLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch sales trend report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | SALES CHANNEL
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchSalesChannelReport.pending,
          (state) => {
            state.salesChannelLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchSalesChannelReport.fulfilled,
          (
            state,
            action
          ) => {
            state.salesChannelLoading =
              false;

            const payload =
              action.payload || {};

            state.salesChannel = {
              dateRange:
                payload.dateRange ||
                null,

              records:
                payload.records ||
                [],

              grandTotal:
                payload.grandTotal ||
                0,
            };
          }
        )
        .addCase(
          fetchSalesChannelReport.rejected,
          (
            state,
            action
          ) => {
            state.salesChannelLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch sales channel report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | PAYMENT METHOD
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchPaymentMethodReport.pending,
          (state) => {
            state.paymentMethodLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchPaymentMethodReport.fulfilled,
          (
            state,
            action
          ) => {
            state.paymentMethodLoading =
              false;

            const payload =
              action.payload || {};

            state.paymentMethod = {
              dateRange:
                payload.dateRange ||
                null,

              records:
                payload.records ||
                [],

              grandTotal:
                payload.grandTotal ||
                0,
            };
          }
        )
        .addCase(
          fetchPaymentMethodReport.rejected,
          (
            state,
            action
          ) => {
            state.paymentMethodLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch payment method report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | OUTSTANDING SALES
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchOutstandingSalesReport.pending,
          (state) => {
            state.outstandingSalesLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchOutstandingSalesReport.fulfilled,
          (
            state,
            action
          ) => {
            state.outstandingSalesLoading =
              false;

            const payload =
              action.payload || {};

            state.outstandingSales = {
              dateRange:
                payload.dateRange ||
                null,

              records:
                payload.records ||
                [],

              summary: {
                ...state.outstandingSales
                  .summary,
                ...(payload.summary ||
                  {}),
              },

              pagination: {
                ...initialPagination,
                ...(payload.pagination ||
                  {}),
              },
            };
          }
        )
        .addCase(
          fetchOutstandingSalesReport.rejected,
          (
            state,
            action
          ) => {
            state.outstandingSalesLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch outstanding sales report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | CUSTOMER SALES
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchCustomerSalesReport.pending,
          (state) => {
            state.customerSalesLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchCustomerSalesReport.fulfilled,
          (
            state,
            action
          ) => {
            state.customerSalesLoading =
              false;

            const payload =
              action.payload || {};

            state.customerSales = {
              dateRange:
                payload.dateRange ||
                null,

              records:
                payload.records ||
                [],

              summary: {
                ...state.customerSales
                  .summary,
                ...(payload.summary ||
                  {}),
              },

              pagination: {
                ...initialPagination,
                ...(payload.pagination ||
                  {}),
              },
            };
          }
        )
        .addCase(
          fetchCustomerSalesReport.rejected,
          (
            state,
            action
          ) => {
            state.customerSalesLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch customer sales report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | PRODUCT SALES
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchProductSalesReport.pending,
          (state) => {
            state.productSalesLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchProductSalesReport.fulfilled,
          (
            state,
            action
          ) => {
            state.productSalesLoading =
              false;

            const payload =
              action.payload || {};

            state.productSales = {
              dateRange:
                payload.dateRange ||
                null,

              records:
                payload.records ||
                [],

              summary: {
                ...state.productSales
                  .summary,
                ...(payload.summary ||
                  {}),
              },

              pagination: {
                ...initialPagination,
                ...(payload.pagination ||
                  {}),
              },
            };
          }
        )
        .addCase(
          fetchProductSalesReport.rejected,
          (
            state,
            action
          ) => {
            state.productSalesLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch product sales report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | TAX SUMMARY
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchTaxSummaryReport.pending,
          (state) => {
            state.taxSummaryLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchTaxSummaryReport.fulfilled,
          (
            state,
            action
          ) => {
            state.taxSummaryLoading =
              false;

            const payload =
              action.payload || {};

            state.taxSummary = {
              dateRange:
                payload.dateRange ||
                null,

              records:
                payload.records ||
                [],

              summary: {
                ...state.taxSummary
                  .summary,
                ...(payload.summary ||
                  {}),
              },
            };
          }
        )
        .addCase(
          fetchTaxSummaryReport.rejected,
          (
            state,
            action
          ) => {
            state.taxSummaryLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch tax summary report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | SALE RETURN REPORT
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchSaleReturnReport.pending,
          (state) => {
            state.saleReturnReportLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchSaleReturnReport.fulfilled,
          (
            state,
            action
          ) => {
            state.saleReturnReportLoading =
              false;

            const payload =
              action.payload || {};

            state.saleReturnReport = {
              dateRange:
                payload.dateRange ||
                null,

              records:
                payload.records ||
                [],

              summary: {
                ...state.saleReturnReport
                  .summary,
                ...(payload.summary ||
                  {}),
              },

              pagination: {
                ...initialPagination,
                ...(payload.pagination ||
                  {}),
              },
            };
          }
        )
        .addCase(
          fetchSaleReturnReport.rejected,
          (
            state,
            action
          ) => {
            state.saleReturnReportLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch sale return report.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | DOCUMENT TYPE REPORT
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchDocumentTypeReport.pending,
          (state) => {
            state.documentTypeReportLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchDocumentTypeReport.fulfilled,
          (
            state,
            action
          ) => {
            state.documentTypeReportLoading =
              false;

            const payload =
              action.payload || {};

            state.documentTypeReport = {
              dateRange:
                payload.dateRange ||
                null,

              records:
                payload.records ||
                [],
            };
          }
        )
        .addCase(
          fetchDocumentTypeReport.rejected,
          (
            state,
            action
          ) => {
            state.documentTypeReportLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch document type report.";
          }
        );
    },
  });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
  setBillingReportFilters,
  setBillingReportFilter,
  setBillingReportDateRange,
  setBillingReportPage,
  setBillingReportLimit,
  resetBillingReportFilters,

  clearBillingReportError,
  clearSalesOverviewReport,
  clearSalesTrendReport,
  clearOutstandingSalesReport,
  clearCustomerSalesReport,
  clearProductSalesReport,

  resetBillingReportState,
} = billingReportSlice.actions;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const billingReportSelector = (
  state
) => state.billingReport;

export const billingReportFiltersSelector = (
  state
) =>
  state.billingReport.filters;

export const billingReportDropdownsSelector = (
  state
) =>
  state.billingReport.dropdowns;

export const salesOverviewReportSelector = (
  state
) =>
  state.billingReport.overview;

export const salesTrendReportSelector = (
  state
) =>
  state.billingReport.salesTrend;

export const salesChannelReportSelector = (
  state
) =>
  state.billingReport.salesChannel;

export const paymentMethodReportSelector = (
  state
) =>
  state.billingReport.paymentMethod;

export const outstandingSalesReportSelector = (
  state
) =>
  state.billingReport.outstandingSales;

export const customerSalesReportSelector = (
  state
) =>
  state.billingReport.customerSales;

export const productSalesReportSelector = (
  state
) =>
  state.billingReport.productSales;

export const taxSummaryReportSelector = (
  state
) =>
  state.billingReport.taxSummary;

export const saleReturnReportSelector = (
  state
) =>
  state.billingReport
    .saleReturnReport;

export const documentTypeReportSelector = (
  state
) =>
  state.billingReport
    .documentTypeReport;

export const billingReportLoadingSelector = (
  state
) => ({
    dropdownsLoading:
      state.billingReport
        .dropdownsLoading,

    overviewLoading:
      state.billingReport
        .overviewLoading,

    salesTrendLoading:
      state.billingReport
        .salesTrendLoading,

    salesChannelLoading:
      state.billingReport
        .salesChannelLoading,

    paymentMethodLoading:
      state.billingReport
        .paymentMethodLoading,

    outstandingSalesLoading:
      state.billingReport
        .outstandingSalesLoading,

    customerSalesLoading:
      state.billingReport
        .customerSalesLoading,

    productSalesLoading:
      state.billingReport
        .productSalesLoading,

    taxSummaryLoading:
      state.billingReport
        .taxSummaryLoading,

    saleReturnReportLoading:
      state.billingReport
        .saleReturnReportLoading,

    documentTypeReportLoading:
      state.billingReport
        .documentTypeReportLoading,
  });

export const billingDashboardLoadingSelector = (
  state
) =>
  state.billingReport
    .overviewLoading ||
  state.billingReport
    .salesTrendLoading ||
  state.billingReport
    .salesChannelLoading ||
  state.billingReport
    .paymentMethodLoading;

export default billingReportSlice.reducer;